/**
 * UARCampaigns.js
 *
 * WHAT: Campaign list view with create, edit, delete, and launch capabilities for User Access Review campaigns.
 * WHY:  Central hub for compliance administrators to manage the full lifecycle of UAR campaigns --
 *       from initial creation (selecting compliance framework, database type, and databases) through
 *       launching the multi-step campaign workflow. Supports guided mode,
 *       evidence viewing, and campaign closure.
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

import API_CONFIG from '../config/api.config';
import { EvidenceViewer, CloseCampaignModal } from './CampaignEvidence';
import { WideModal, CertificationStatusModal } from './common';
import { formatDateTime } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Main campaign management component rendering the campaign list and create/edit modal.
 * WHY:  Gives compliance admins a single interface to view all campaigns, create new ones
 *       delete/archive drafts, and navigate into the
 *       campaign workflow for attestation preparation.
 */
const UARCampaigns = ({ onCampaignSelect, onNavigateToDashboard, onGoBack, initialAction }) => {
  const { user } = useAuth();
  const [selectedCompliance, setSelectedCompliance] = useState('');
  const [selectedDatabaseType, setSelectedDatabaseType] = useState('');
  const [applications, setApplications] = useState([]);
  const [databaseMappings, setDatabaseMappings] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [filteredDatabases, setFilteredDatabases] = useState([]);
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [campaignStatus, setCampaignStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [baseCampaignName, setBaseCampaignName] = useState('');
  const [existingCampaigns, setExistingCampaigns] = useState([]);
  const [campaignsExist, setCampaignsExist] = useState(false);
  const [showCampaignListPage, setShowCampaignListPage] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [complianceOptions, setComplianceOptions] = useState([]);
  const [databaseTypeOptions, setDatabaseTypeOptions] = useState([]);
  const [showCreateCampaignForm, setShowCreateCampaignForm] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteAction, setDeleteAction] = useState('archive');
  const [tableName, setTableName] = useState('');
  const [showCampaignActionDropdown, setShowCampaignActionDropdown] = useState(false);
  const [showCertificationErrorModal, setShowCertificationErrorModal] = useState(false);
  const [certificationError, setCertificationError] = useState({});
  const [showConfirmCampaignModal, setShowConfirmCampaignModal] = useState(false);
  const [confirmValidationError, setConfirmValidationError] = useState('');
  const [showMissingConnectionsModal, setShowMissingConnectionsModal] = useState(false);
  const [missingConnectionsData, setMissingConnectionsData] = useState({ missing: [], campaignData: null, campaignName: '' });

  // Evidence Viewer state
  const [showEvidenceViewer, setShowEvidenceViewer] = useState(false);
  const [evidenceViewerCampaign, setEvidenceViewerCampaign] = useState(null);

  // Close Campaign Modal state
  const [showCloseCampaignModal, setShowCloseCampaignModal] = useState(false);
  const [closeCampaignTarget, setCloseCampaignTarget] = useState(null);

  // Closure readiness state -- tracks which ATTESTATION campaigns are ready to close
  const [closureReadiness, setClosureReadiness] = useState({});

  // Track current action to prevent stale async callbacks
  const currentActionRef = useRef(initialAction);

  // Guide Me state
  const [guideMode, setGuideMode] = useState(false);

  // Guide Me step definitions
  const guideSteps = [
    {
      step: 1,
      title: 'Select Compliance Framework',
      description: 'Choose the regulatory standard for your campaign (e.g., SOX, PCI-DSS, HIPAA)',
      isComplete: () => selectedCompliance !== '',
      target: 'compliance'
    },
    {
      step: 2,
      title: 'Select Database Type',
      description: 'Choose which database technology to include in this campaign',
      isComplete: () => selectedDatabaseType !== '',
      target: 'databaseType'
    },
    {
      step: 3,
      title: 'Select Databases',
      description: 'Check the databases you want to include in your campaign',
      isComplete: () => selectedDatabases.length > 0,
      target: 'databases'
    },
    {
      step: 4,
      title: 'Create Campaign',
      description: 'Click the button below to finalize your campaign',
      isComplete: () => false,
      target: 'create'
    }
  ];

  // Get current guide step based on progress
  const getCurrentGuideStep = () => {
    for (const step of guideSteps) {
      if (!step.isComplete()) {
        return step.step;
      }
    }
    return 4; // All complete, show create button
  };

  // Start Guide Me mode
  const startGuideMe = () => {
    // Reset selections when starting guide
    setSelectedCompliance('');
    setSelectedDatabaseType('');
    setSelectedDatabases([]);
    setFilteredDatabases([]);
    setGuideMode(true);
  };

  // Exit Guide Me mode
  const exitGuideMe = () => {
    setGuideMode(false);
  };

  useEffect(() => {
    fetchApplications();
    fetchDatabaseMappings();
    checkExistingCampaigns();
  }, []);

  // Handle initial action from sidebar navigation
  useEffect(() => {
    let cancelled = false;
    currentActionRef.current = initialAction;

    // Reset all page states when navigating via sidebar
    setShowCreateCampaignForm(false);
    setShowCampaignListPage(false);
    setShowCampaignActionDropdown(false);

    if (initialAction === 'create') {
      setShowCreateCampaignForm(true);
      fetchComplianceOptions();
      fetchDatabaseTypeOptions();
    } else if (initialAction === 'select') {
      // Inline async to use effect cleanup cancellation
      (async () => {
        setIsLoading(true);
        setCampaignStatus('Loading existing campaigns...');
        try {
          await checkExistingCampaigns();
          if (cancelled) return;
          setSelectedCampaign(null);
          setShowCampaignListPage(true);
          setCampaignStatus('');
        } catch (error) {
          if (cancelled) return;
          setCampaignStatus('Error loading existing campaigns. Please try again.');
          console.error('Campaign loading error:', error);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })();
    }

    return () => { cancelled = true; };
  }, [initialAction]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCampaignActionDropdown && !event.target.closest('.dropdown')) {
        setShowCampaignActionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCampaignActionDropdown]);

  useEffect(() => {
    filterApplications();
    filterDatabases();
  }, [selectedCompliance, selectedDatabaseType, applications, databaseMappings]);

  // Auto-generate campaign name when both compliance and database type are selected
  useEffect(() => {
    if (selectedCompliance && selectedDatabaseType) {
      const quarterYear = getCurrentQuarterAndYear();
      const complianceLabel = getComplianceLabel(selectedCompliance);
      const suffix = `_${complianceLabel}_${selectedDatabaseType}`;
      const fullCampaignName = quarterYear + suffix;
      setBaseCampaignName(quarterYear);
      setCampaignName(fullCampaignName);
      setTableName(generateTableName(fullCampaignName));
    }
  }, [selectedCompliance, selectedDatabaseType, complianceOptions]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('applications/'));
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const fetchDatabaseMappings = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('app-database-mappings/'));
      setDatabaseMappings(response.data);
    } catch (error) {
      console.error('Error fetching database mappings:', error);
    }
  };

  const checkExistingCampaigns = async () => {
    try {
      // Build query params with user context for ownership filtering
      const params = new URLSearchParams();
      if (user?.employee_id) {
        params.append('user_employee_id', user.employee_id);
      }
      if (user?.role?.is_dev_admin || user?.role?.can_manage_campaigns) {
        params.append('is_admin', 'true');
      }
      // Enable filtering by owner for campaign admins (not dev admins)
      if (user?.role?.can_manage_campaigns && !user?.role?.is_dev_admin) {
        params.append('filter_by_owner', 'true');
      }

      const url = `${API_CONFIG.getDatabasesEndpoint('campaigns/')}?${params.toString()}`;
      const response = await axios.get(url);
      if (response.data.success) {
        setExistingCampaigns(response.data.campaigns);
        setCampaignsExist(response.data.count > 0);
        // Fetch closure readiness for campaigns in ATTESTATION status
        fetchClosureReadiness(response.data.campaigns);
      }
    } catch (error) {
      console.error('Error checking existing campaigns:', error);
      setCampaignsExist(false);
    }
  };

  // Fetch closure readiness for all ATTESTATION campaigns to enable/disable Close buttons
  const fetchClosureReadiness = async (campaigns) => {
    const attestationCampaigns = campaigns.filter(c => c.status === 'ATTESTATION');
    if (attestationCampaigns.length === 0) return;

    const readinessMap = {};
    await Promise.all(
      attestationCampaigns.map(async (campaign) => {
        try {
          const response = await axios.get(
            API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/closure-readiness/`)
          );
          if (response.data.success) {
            readinessMap[campaign.campaign_id] = response.data;
          }
        } catch (error) {
          console.error(`Error checking readiness for campaign ${campaign.campaign_id}:`, error);
        }
      })
    );
    setClosureReadiness(readinessMap);
  };

  const fetchComplianceOptions = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('config/compliance-frameworks/'));
      if (response.data.success) {
        setComplianceOptions(response.data.frameworks);
      }
    } catch (error) {
      console.error('Error fetching compliance options:', error);
      // Fallback to hardcoded options if API fails
      setComplianceOptions([
        { value: 'sox_compliant', label: 'SOX', description: 'Sarbanes-Oxley compliance' },
        { value: 'gdpr_compliant', label: 'GDPR', description: 'General Data Protection Regulation' },
        { value: 'pci_compliant', label: 'PCI', description: 'Payment Card Industry' },
        { value: 'hipaa_compliant', label: 'HIPAA', description: 'Health Insurance Portability' },
        { value: 'iso27001_compliant', label: 'ISO 27001', description: 'Information Security Management' }
      ]);
    }
  };

  const fetchDatabaseTypeOptions = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('config/database-types/'));
      if (response.data.success) {
        setDatabaseTypeOptions(response.data.database_types);
      }
    } catch (error) {
      console.error('Error fetching database type options:', error);
      // Fallback to hardcoded options if API fails
      setDatabaseTypeOptions([
        { value: 'Oracle', label: 'Oracle', description: 'Oracle Database' },
        { value: 'PostgreSQL', label: 'PostgreSQL', description: 'PostgreSQL Database' },
        { value: 'MySQL', label: 'MySQL', description: 'MySQL Database' }
      ]);
    }
  };

  const filterApplications = () => {
    if (!selectedCompliance && !selectedDatabaseType) {
      setFilteredApps([]);
      return;
    }

    let filtered = applications;

    // Filter by compliance if selected
    if (selectedCompliance) {
      filtered = filtered.filter(app => {
        try {
          const complianceFlags = JSON.parse(app.compliance_flags || '{}');
          return complianceFlags[selectedCompliance] === 'true';
        } catch (error) {
          console.error('Error parsing compliance flags:', error);
          return false;
        }
      });
    }

    // Filter by database type if selected
    if (selectedDatabaseType) {
      const appIdsWithDatabaseType = databaseMappings
        .filter(mapping => mapping.database_type.toUpperCase() === selectedDatabaseType.toUpperCase())
        .map(mapping => mapping.app_id);

      filtered = filtered.filter(app => appIdsWithDatabaseType.includes(app.app_id));
    }

    setFilteredApps(filtered);
  };

  // WHAT: Load the AUTHORITATIVE list of databases eligible for a new campaign of
  //       (selectedDatabaseType, selectedCompliance) from the backend.
  // WHY:  The eligible set = app mandates the framework AND the DB is in compliance
  //       scope (DB Compliance Matrix override) AND has an established connection.
  //       The compliance-scope override and connection presence live server-side, so
  //       we ask the backend rather than re-deriving them in the browser. This keeps
  //       out-of-scope / unconnected DBs (e.g. Oracle ASSETPROD01) out of the picker.
  const filterDatabases = async () => {
    if (!selectedDatabaseType || !selectedCompliance) {
      setFilteredDatabases([]);
      return;
    }
    try {
      // selectedCompliance holds the option key (e.g. 'sox_compliant'), but the
      // eligible-databases endpoint keys off the framework LABEL ('SOX') — same as
      // create_campaign (which sends getComplianceLabel(...)). Convert here so the
      // picker isn't silently empty for every framework.
      const params = new URLSearchParams({
        database_type: selectedDatabaseType,
        compliance_framework: getComplianceLabel(selectedCompliance),
      });
      const response = await axios.get(
        `${API_CONFIG.getDatabasesEndpoint('campaigns/eligible-databases/')}?${params.toString()}`
      );
      if (response.data && response.data.success) {
        // Enrich with app_name from already-loaded applications when present.
        const databases = (response.data.databases || []).map(db => {
          const app = applications.find(a => a.app_id === db.app_id);
          return { ...db, app_name: app ? app.app_name : (db.app_name || 'Unknown Application') };
        });
        setFilteredDatabases(databases);
        return;
      }
      setFilteredDatabases([]);
    } catch (error) {
      console.error('Failed to load eligible campaign databases:', error);
      setFilteredDatabases([]);
    }
  };

  const handleComplianceChange = (complianceType) => {
    console.log('=== handleComplianceChange ===');
    console.log('New compliance:', complianceType);
    console.log('Old compliance:', selectedCompliance);
    console.log('Current database type:', selectedDatabaseType);
    setSelectedCompliance(complianceType);
    // Clear selected databases
    setSelectedDatabases([]);
    setCampaignStatus('');
    console.log('After setting compliance, database type should still be:', selectedDatabaseType);
  };

  const handleDatabaseTypeChange = (databaseType) => {
    console.log('=== handleDatabaseTypeChange ===');
    console.log('New database type:', databaseType);
    console.log('Old database type:', selectedDatabaseType);
    console.log('Current compliance:', selectedCompliance);
    setSelectedDatabaseType(databaseType);
    // Clear selected databases when database type changes
    setSelectedDatabases([]);
    setCampaignStatus('');
    console.log('After setting database type, compliance should still be:', selectedCompliance);
  };

  const handleDatabaseSelection = (database, isSelected) => {
    const databaseKey = `${database.database_name}-${database.app_id}`;

    if (isSelected) {
      setSelectedDatabases(prev => [...prev, databaseKey]);
    } else {
      setSelectedDatabases(prev => prev.filter(key => key !== databaseKey));
    }
  };

  const handleSelectAll = () => {
    if (selectedDatabases.length === filteredDatabases.length) {
      // Deselect all
      setSelectedDatabases([]);
    } else {
      // Select all
      const allDatabaseKeys = filteredDatabases.map(db => `${db.database_name}-${db.app_id}`);
      setSelectedDatabases(allDatabaseKeys);
    }
  };

  const isDatabaseSelected = (database) => {
    const databaseKey = `${database.database_name}-${database.app_id}`;
    return selectedDatabases.includes(databaseKey);
  };

  const handleCampaignNameChange = (e) => {
    // Editable region is the base prefix only — the suffix is rendered as a
    // locked label next to the input. Compose the full campaign name from
    // base + suffix here so the rest of the flow (validation, API submit,
    // table-name derivation) keeps working off `campaignName`.
    // DI-18: enforce the campaign-name rule at the point of entry — allow ONLY
    // letters, numbers, spaces, hyphens, and underscores. Anything else is
    // stripped as the user types, so a disallowed character can never be
    // submitted. The backend (create_campaign) re-validates and is the real
    // enforcement point; this is the UX mirror.
    const baseName = e.target.value.replace(/[^A-Za-z0-9 _-]/g, '');
    const complianceLabel = getComplianceLabel(selectedCompliance);
    const suffix = `_${complianceLabel}_${selectedDatabaseType}`;
    const fullName = baseName + suffix;
    setBaseCampaignName(baseName);
    setCampaignName(fullName);
    setTableName(generateTableName(fullName));
  };

  const handleTableNameChange = (e) => {
    const inputValue = e.target.value;
    // Allow only alphanumeric and underscores, convert to uppercase
    const cleanValue = inputValue
      .replace(/[^a-zA-Z0-9_]/g, '')
      .toUpperCase();
    setTableName(cleanValue);
  };

  const generateTableName = (campName) => {
    // Generate table name: uar_report_<campaignname>
    // DI-18: this preview MUST match the backend derivation byte-for-byte
    // (databases/utils/report_table_name.derive_report_table_name), otherwise
    // the user sees a table name different from the one actually created, and
    // two names that collide on the backend ('Q1 SOX' vs 'Q1  SOX') could look
    // identical here. Backend algorithm = lowercase, replace space/hyphen with
    // '_', then map every remaining non-alnum/non-'_' char to '_'. It does NOT
    // collapse repeated underscores and does NOT strip leading/trailing '_'.
    const cleanName = campName
      .toLowerCase()
      .replace(/ /g, '_')
      .replace(/-/g, '_')
      .replace(/[^a-z0-9_]/g, '_');
    return `uar_report_${cleanName}`;
  };

  const handleCloseCreateModal = () => {
    setShowCreateCampaignForm(false);
    setShowConfirmCampaignModal(false);
    setSelectedCompliance('');
    setSelectedDatabaseType('');
    setSelectedDatabases([]);
    setFilteredDatabases([]);
    setCampaignName('');
    setBaseCampaignName('');
    setTableName('');
    setCampaignStatus('');
    setGuideMode(false);
    setConfirmValidationError('');
    if (onGoBack) {
      onGoBack();
    }
  };

  const confirmStartCampaign = () => {
    setConfirmValidationError('');
    setShowConfirmCampaignModal(true);
  };

  const executeCreateCampaign = async () => {
    // Validate fields
    if (!campaignName.trim()) {
      setConfirmValidationError('Please enter a campaign name.');
      return;
    }
    if (!tableName.trim()) {
      setConfirmValidationError('Please enter a report table name.');
      return;
    }

    const confirmedCampaignName = campaignName.trim();
    const confirmedTableName = tableName.trim();

    const selectedDatabasesData = filteredDatabases
      .filter(db => {
        const databaseKey = `${db.database_name}-${db.app_id}`;
        return selectedDatabases.includes(databaseKey);
      })
      .map(db => ({
        database_name: db.database_name,
        database_identifier: db.database_identifier,
        app_id: db.app_id,
        app_name: db.app_name,
        database_type: db.database_type,
        database_environment: db.database_environment,
        database_host: db.database_host,
        database_version: db.database_version
      }));

    setShowConfirmCampaignModal(false);
    setIsLoading(true);

    // Show loading modal
    Swal.fire({
      title: 'Checking privilege certification...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // First, check if all privileges for this database type are certified
      const certificationCheck = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: selectedDatabaseType } }
      );

      if (!certificationCheck.data.all_certified) {
        Swal.close();
        setIsLoading(false);
        setCertificationError({
          databaseType: selectedDatabaseType,
          uncertifiedCount: certificationCheck.data.uncertified_count,
          certifiedCount: certificationCheck.data.certified_count,
          totalCount: certificationCheck.data.total_count,
          // Roles are a separate population (MSSQL fixed roles). Surface them
          // so the modal explains a roles-only blocker (privileges all done).
          roleUncertifiedCount: certificationCheck.data.role_uncertified_count || 0,
          roleUnassignedCount: certificationCheck.data.role_unassigned_count || 0,
          roleCertifiedCount: certificationCheck.data.role_certified_count || 0,
          roleTotalCount: certificationCheck.data.role_total_count || 0,
        });
        setShowCertificationErrorModal(true);
        return;
      }

      // Update loading message
      Swal.update({
        title: 'Creating campaign...'
      });

      // Prepare campaign data
      const campaignData = {
        campaign_name: confirmedCampaignName,
        campaign_table_name: confirmedTableName,
        campaign_description: `UAR Campaign for ${getComplianceLabel(selectedCompliance)} compliance targeting ${selectedDatabaseType} databases`,
        compliance_framework: getComplianceLabel(selectedCompliance),
        database_type: selectedDatabaseType,
        selected_databases: selectedDatabasesData,
        created_by: user?.employee_id || 'unknown_user'
      };

      // Call the API to create the campaign
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('campaigns/create/'), campaignData);

      // Check if there are missing connections that require confirmation
      if (response.data.requires_confirmation) {
        Swal.close();
        setIsLoading(false);
        setMissingConnectionsData({
          missing: response.data.missing_connections || [],
          campaignData,
          campaignName: confirmedCampaignName
        });
        setShowMissingConnectionsModal(true);
        return;
      }

      if (response.data.success) {
        const itemCount = selectedDatabasesData.length;
        const createdCampaignId = response.data.campaign_id;

        // Refresh existing campaigns
        checkExistingCampaigns();

        // Reset form
        setShowCreateCampaignForm(false);
        setSelectedDatabases([]);
        setSelectedCompliance('');
        setSelectedDatabaseType('');
        setFilteredDatabases([]);
        setCampaignName('');
        setTableName('');
        setCampaignStatus('');

        // Show success modal with action button
        Swal.fire({
          title: 'Campaign Created Successfully!',
          html: `
            <div style="text-align: left; padding: 10px 0;">
              <p style="margin-bottom: 15px; font-size: 1rem;">
                <strong>"${confirmedCampaignName}"</strong><br/>
                Campaign ID: <strong>#${createdCampaignId}</strong> • ${itemCount} database${itemCount !== 1 ? 's' : ''}
              </p>
            </div>
          `,
          icon: 'success',
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          confirmButtonText: '<i class="fas fa-list me-2"></i>Go to Existing Campaigns',
          cancelButtonText: 'Close'
        }).then((result) => {
          if (result.isConfirmed) {
            setShowCreateCampaignForm(false);
            selectExistingCampaign();
          } else if (onGoBack) {
            onGoBack();
          }
        });
      } else {
        const errorMsg = response.data.error || response.data.message || 'Error creating campaign. Please try again.';
        Swal.fire({
          title: 'Campaign Creation Failed',
          text: errorMsg,
          icon: 'error',
          confirmButtonColor: '#dc3545',
          confirmButtonText: 'OK',
          didOpen: (popup) => {
            const container = popup.parentElement;
            if (container) container.style.zIndex = '999999';
          }
        }).then(() => {
          setShowConfirmCampaignModal(true);
        });
        setCampaignStatus('');
      }
    } catch (error) {
      console.error('Campaign creation error:', error);

      // Display the actual error message from the backend if available
      let errorMsg = 'Error creating campaign. Please check your connection and try again.';
      if (error.response && error.response.data && error.response.data.error) {
        errorMsg = error.response.data.error;
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      Swal.fire({
        title: 'Campaign Creation Failed',
        text: errorMsg,
        icon: 'error',
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'OK',
        didOpen: (popup) => {
          const container = popup.parentElement;
          if (container) container.style.zIndex = '999999';
        }
      }).then(() => {
        setShowConfirmCampaignModal(true);
      });
      setCampaignStatus('');

      if (error.response) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedWithMissingConnections = async () => {
    const { campaignData, campaignName: confirmedName, missing } = missingConnectionsData;
    setShowMissingConnectionsModal(false);
    setIsLoading(true);

    Swal.fire({
      title: 'Creating campaign...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading(); }
    });

    try {
      const confirmedResponse = await axios.post(
        API_CONFIG.getDatabasesEndpoint('campaigns/create/'),
        { ...campaignData, confirm_missing_connections: true }
      );

      if (confirmedResponse.data.success) {
        const itemCount = campaignData.selected_databases.length;
        const createdCampaignId = confirmedResponse.data.campaign_id;

        checkExistingCampaigns();

        setShowCreateCampaignForm(false);
        setSelectedDatabases([]);
        setSelectedCompliance('');
        setSelectedDatabaseType('');
        setFilteredDatabases([]);
        setCampaignName('');
        setTableName('');
        setCampaignStatus('');

        Swal.fire({
          title: 'Campaign Created Successfully!',
          html: `
            <div style="text-align: left; padding: 10px 0;">
              <p style="margin-bottom: 15px; font-size: 1rem;">
                <strong>"${confirmedName}"</strong><br/>
                Campaign ID: <strong>#${createdCampaignId}</strong> • ${itemCount} database${itemCount !== 1 ? 's' : ''}
              </p>
              <div class="alert alert-info mb-0">
                <i class="fas fa-info-circle me-2"></i>
                <small><strong>Note:</strong> ${missing.length} database(s) have missing connections and won't have data until configured.</small>
              </div>
            </div>
          `,
          icon: 'success',
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          confirmButtonText: '<i class="fas fa-list me-2"></i>Go to Existing Campaigns',
          cancelButtonText: 'Close'
        }).then((result) => {
          if (result.isConfirmed) {
            setShowCreateCampaignForm(false);
            selectExistingCampaign();
          } else if (onGoBack) {
            onGoBack();
          }
        });
      } else {
        const errorMsg = confirmedResponse.data.error || confirmedResponse.data.message || 'Error creating campaign.';
        Swal.fire({
          title: 'Campaign Creation Failed',
          text: errorMsg,
          icon: 'error',
          confirmButtonColor: '#dc3545',
          confirmButtonText: 'OK'
        });
        setCampaignStatus('');
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.error || error.message || 'Failed to create campaign.',
        icon: 'error',
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectExistingCampaign = async () => {
    const actionAtStart = currentActionRef.current;
    setIsLoading(true);
    setCampaignStatus('Loading existing campaigns...');

    try {
      await checkExistingCampaigns(); // Refresh campaign list
      // Only update UI if the action hasn't changed while we were loading
      if (currentActionRef.current !== actionAtStart) return;
      setSelectedCampaign(null); // Clear previous selection
      setShowCampaignListPage(true);
      setCampaignStatus('');
    } catch (error) {
      if (currentActionRef.current !== actionAtStart) return;
      setCampaignStatus('❌ Error loading existing campaigns. Please try again.');
      console.error('Campaign loading error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCampaignSelection = (campaignId) => {
    // For radio button behavior - only one campaign can be selected
    setSelectedCampaign(campaignId);
  };

  const isDeletableCampaign = (campaign) => {
    // Campaign must not be completed AND user must have permission to modify it
    return campaign.status !== 'COMPLETED' && campaign.can_modify !== false;
  };

  const isSelectedCampaignDeletable = () => {
    if (!selectedCampaign) return false;
    const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
    return campaign && isDeletableCampaign(campaign);
  };

  const canModifySelectedCampaign = () => {
    if (!selectedCampaign) return false;
    const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
    return campaign && campaign.can_modify !== false;
  };

  const showDeleteConfirmation = () => {
    if (!selectedCampaign || !isSelectedCampaignDeletable()) {
      alert('No deletable campaign selected.');
      return;
    }
    setDeleteAction('archive'); // Default to archive
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!selectedCampaign || !isSelectedCampaignDeletable()) {
      alert('No deletable campaign selected.');
      return;
    }

    const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
    const campaignName = campaign?.campaign_name;

    setShowDeleteConfirmModal(false);
    setIsLoading(true);

    const actionLabel = deleteAction === 'archive' ? 'archiving' : 'removing';
    setCampaignStatus(`Deleting campaign "${campaignName}" (${actionLabel} table)...`);

    try {
      const response = await axios.delete(API_CONFIG.getDatabasesEndpoint('campaigns/delete/'), {
        data: {
          campaign_ids: [selectedCampaign],
          action: deleteAction,
          user_employee_id: user?.employee_id || '',
          is_admin: user?.role?.is_dev_admin || false
        }
      });

      if (response.data.success) {
        const tableInfo = response.data.table_actions && response.data.table_actions.length > 0
          ? ` Table ${deleteAction === 'archive' ? 'archived as ' + response.data.table_actions[0].archived_as : 'removed permanently'}.`
          : '';
        setCampaignStatus(`✅ Campaign "${campaignName}" deleted successfully.${tableInfo}`);
        setSelectedCampaign(null); // Clear selection
        await checkExistingCampaigns(); // Refresh campaign list
        // If no campaigns left, close the page
        if (existingCampaigns.length === 1) {
          setShowCampaignListPage(false);
        }
      } else {
        setCampaignStatus('❌ Error deleting campaign. Please try again.');
      }
    } catch (error) {
      setCampaignStatus('❌ Error deleting campaign. Please check your connection and try again.');
      console.error('Campaign deletion error:', error);
      if (error.response && error.response.data.error) {
        setCampaignStatus(`❌ ${error.response.data.error}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getComplianceLabel = (complianceType) => {
    const option = complianceOptions.find(opt => (opt.key || opt.value) === complianceType);
    return option ? option.label : complianceType;
  };

  const getCurrentQuarterAndYear = () => {
    const now = new Date();
    const month = now.getMonth(); // 0-11
    const year = now.getFullYear();

    let quarter;
    if (month < 3) {
      quarter = 'Q1';
    } else if (month < 6) {
      quarter = 'Q2';
    } else if (month < 9) {
      quarter = 'Q3';
    } else {
      quarter = 'Q4';
    }

    return `${quarter}_${year}`;
  };

  return (
    <div className="container-fluid mt-4">
      {/* Create Campaign WideModal */}
      <WideModal
        show={showCreateCampaignForm && !showCampaignListPage}
        onClose={handleCloseCreateModal}
        title="Create New Campaign"
        headerIcon="fa-plus-circle"
        headerClass="bg-success text-white"
        maxWidth="1600px"
        bodyStyle={{ minHeight: '75vh' }}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseCreateModal}
            >
              <i className="fas fa-times me-2"></i>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => {
                if (guideMode) {
                  exitGuideMe();
                }
                confirmStartCampaign();
              }}
              disabled={!selectedCompliance || !selectedDatabaseType || selectedDatabases.length === 0 || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-plus me-2"></i>
                  Create Campaign{selectedDatabases.length > 0 ? ` (${selectedDatabases.length} database${selectedDatabases.length !== 1 ? 's' : ''})` : ''}
                </>
              )}
            </button>
          </>
        }
      >
            {/* Guide Me Header */}
            {guideMode && (
              <div className="mb-3 p-3" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-compass me-2" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                    <h5 className="mb-0 text-white">Guide Me: Create New Campaign</h5>
                  </div>
                  <button
                    className="btn btn-outline-light btn-sm"
                    onClick={exitGuideMe}
                  >
                    <i className="fas fa-times me-1"></i>
                    Exit Guide
                  </button>
                </div>

                {/* Step Indicator */}
                <div className="d-flex justify-content-between align-items-center">
                  {guideSteps.map((step, index) => {
                    const currentStep = getCurrentGuideStep();
                    const isActive = step.step === currentStep;
                    const isCompleted = step.step < currentStep;

                    return (
                      <div key={step.step} className="d-flex align-items-center" style={{ flex: 1 }}>
                        <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isCompleted ? '#28a745' : isActive ? 'white' : 'rgba(255,255,255,0.3)',
                            color: isCompleted ? 'white' : isActive ? '#667eea' : 'rgba(255,255,255,0.7)',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            transition: 'all 0.3s ease',
                            boxShadow: isActive ? '0 0 0 4px rgba(255,255,255,0.4)' : 'none'
                          }}>
                            {isCompleted ? <i className="fas fa-check"></i> : step.step}
                          </div>
                          <small className="text-white mt-1" style={{
                            opacity: isActive ? 1 : 0.7,
                            fontWeight: isActive ? '600' : '400',
                            fontSize: '0.75rem',
                            textAlign: 'center'
                          }}>
                            {step.title}
                          </small>
                        </div>
                        {index < guideSteps.length - 1 && (
                          <div style={{
                            height: '2px',
                            flex: 1,
                            background: isCompleted ? '#28a745' : 'rgba(255,255,255,0.3)',
                            margin: '0 8px',
                            marginBottom: '20px'
                          }}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Guide Me Button (when not in guide mode) */}
            {!guideMode && (
              <div className="mb-3 p-3 d-flex justify-content-between align-items-center" style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '10px',
                border: '1px dashed #667eea'
              }}>
                <div>
                  <h6 className="mb-1" style={{ color: '#495057' }}>
                    <i className="fas fa-lightbulb me-2" style={{ color: '#ffc107' }}></i>
                    Need help creating a campaign?
                  </h6>
                  <small className="text-muted">Let us guide you through the process step by step</small>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={startGuideMe}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    padding: '10px 20px'
                  }}
                >
                  <i className="fas fa-compass me-2"></i>
                  Guide Me
                </button>
              </div>
            )}

            {/* Two-column layout */}
            <div className="row" style={{ minHeight: '65vh' }}>
              {/* LEFT COLUMN - Selections + Campaign Details */}
              <div className="col-lg-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Compliance Selection */}
                <div className="mb-3" data-tour="compliance-section" style={guideMode && getCurrentGuideStep() === 1 ? {
                  border: '2px solid #667eea',
                  borderRadius: '12px',
                  padding: '15px',
                  background: 'rgba(102, 126, 234, 0.05)',
                  boxShadow: '0 0 20px rgba(102, 126, 234, 0.2)'
                } : {}}>
                  {/* Guide Me Prompt */}
                  {guideMode && getCurrentGuideStep() === 1 && (
                    <div className="mb-3 p-3" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      color: 'white'
                    }}>
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          <i className="fas fa-hand-pointer" style={{ fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                          <strong>Step 1: {guideSteps[0].title}</strong>
                          <p className="mb-0" style={{ opacity: 0.9, fontSize: '0.9rem' }}>{guideSteps[0].description}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      <i className="fas fa-shield-alt me-2"></i>
                      Compliance Framework
                      {guideMode && getCurrentGuideStep() > 1 && (
                        <i className="fas fa-check-circle ms-2 text-success"></i>
                      )}
                    </h6>
                    {(selectedCompliance || selectedDatabaseType) && (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          setSelectedCompliance('');
                          setSelectedDatabaseType('');
                          setSelectedDatabases([]);
                          setFilteredDatabases([]);
                        }}
                      >
                        <i className="fas fa-times me-1"></i>
                        Clear
                      </button>
                    )}
                  </div>
                  {complianceOptions.map((option) => (
                    <div key={option.value} className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="complianceType"
                        id={option.key || option.value}
                        value={option.key || option.value}
                        checked={selectedCompliance === (option.key || option.value)}
                        onChange={(e) => handleComplianceChange(e.target.value)}
                      />
                      <label className="form-check-label" htmlFor={option.key || option.value}>
                        <strong>{option.label}</strong>
                        <small className="text-muted ms-1">{option.description}</small>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Database Type Selection */}
                <div className="mb-3" data-tour="database-type-section" style={guideMode && getCurrentGuideStep() === 2 ? {
                  border: '2px solid #667eea',
                  borderRadius: '12px',
                  padding: '15px',
                  background: 'rgba(102, 126, 234, 0.05)',
                  boxShadow: '0 0 20px rgba(102, 126, 234, 0.2)'
                } : guideMode && getCurrentGuideStep() < 2 ? {
                  opacity: 0.5,
                  pointerEvents: 'none'
                } : {}}>
                  {/* Guide Me Prompt */}
                  {guideMode && getCurrentGuideStep() === 2 && (
                    <div className="mb-3 p-3" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      color: 'white'
                    }}>
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          <i className="fas fa-hand-pointer" style={{ fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                          <strong>Step 2: {guideSteps[1].title}</strong>
                          <p className="mb-0" style={{ opacity: 0.9, fontSize: '0.9rem' }}>{guideSteps[1].description}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <h6 className="mb-2">
                    <i className="fas fa-database me-2"></i>
                    Database Type
                    {guideMode && getCurrentGuideStep() > 2 && (
                      <i className="fas fa-check-circle ms-2 text-success"></i>
                    )}
                    {!selectedCompliance && !guideMode && (
                      <small className="text-warning ms-2">
                        (Select compliance first)
                      </small>
                    )}
                  </h6>
                  {databaseTypeOptions.map((option) => (
                    <div key={option.value} className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="databaseType"
                        id={option.key || option.label}
                        value={option.key || option.label}
                        checked={selectedDatabaseType === (option.key || option.label)}
                        onChange={(e) => handleDatabaseTypeChange(e.target.value)}
                        disabled={!selectedCompliance}
                      />
                      <label className={`form-check-label ${!selectedCompliance ? 'text-muted' : ''}`} htmlFor={option.key || option.label}>
                        <strong>{option.label}</strong>
                        <small className="text-muted ms-1">{option.description}</small>
                      </label>
                    </div>
                  ))}
                </div>

              </div>

              {/* RIGHT COLUMN - Filtered Databases */}
              <div className="col-lg-8" data-tour="database-table" style={guideMode && getCurrentGuideStep() === 3 ? {
                border: '2px solid #667eea',
                borderRadius: '12px',
                padding: '15px',
                background: 'rgba(102, 126, 234, 0.05)',
                boxShadow: '0 0 20px rgba(102, 126, 234, 0.2)'
              } : {}}>
                {/* Guide Me Prompt */}
                {guideMode && getCurrentGuideStep() === 3 && (
                  <div className="mb-3 p-3" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '8px',
                    color: 'white'
                  }}>
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        <i className="fas fa-hand-pointer" style={{ fontSize: '1.2rem' }}></i>
                      </div>
                      <div>
                        <strong>Step 3: {guideSteps[2].title}</strong>
                        <p className="mb-0" style={{ opacity: 0.9, fontSize: '0.9rem' }}>{guideSteps[2].description}</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedCompliance && selectedDatabaseType ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Filtered Databases ({filteredDatabases.length})
                        {guideMode && getCurrentGuideStep() > 3 && (
                          <i className="fas fa-check-circle ms-2 text-success"></i>
                        )}
                        <small className="text-muted ms-2">
                          {getComplianceLabel(selectedCompliance)} + {selectedDatabaseType}
                        </small>
                      </h6>
                    </div>

                    {filteredDatabases.length > 0 ? (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {selectedDatabases.length} of {filteredDatabases.length} databases selected
                          </span>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleSelectAll}
                            disabled={filteredDatabases.length === 0}
                          >
                            <i className="fas fa-check-square me-1"></i>
                            {selectedDatabases.length === filteredDatabases.length ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                        <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                          <table className="table table-sm table-striped table-hover mb-0">
                            <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th style={{width: '40px'}}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedDatabases.length === filteredDatabases.length && filteredDatabases.length > 0}
                                    onChange={handleSelectAll}
                                    title="Select/Deselect All"
                                  />
                                </th>
                                <th>Database Name</th>
                                <th>Application</th>
                                <th>Environment</th>
                                <th>Host</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredDatabases.map((db) => (
                                <tr key={`${db.database_name}-${db.app_id}`} className={isDatabaseSelected(db) ? 'table-info' : ''}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isDatabaseSelected(db)}
                                      onChange={(e) => handleDatabaseSelection(db, e.target.checked)}
                                    />
                                  </td>
                                  <td>
                                    <strong>{db.database_name}</strong>
                                  </td>
                                  <td>
                                    <span>{db.app_name}</span>
                                  </td>
                                  <td>
                                    <span className={`badge ${
                                      db.database_environment === 'PRODUCTION' ? 'bg-danger' :
                                      db.database_environment === 'TEST' ? 'bg-warning' :
                                      db.database_environment === 'STAGING' ? 'bg-info' :
                                      'bg-secondary'
                                    }`}>
                                      {db.database_environment}
                                    </span>
                                  </td>
                                  <td>
                                    <small className="text-muted">{db.database_host}</small>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderRadius: '12px',
                        border: '2px dashed #dee2e6'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          margin: '0 auto 15px',
                          background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="fas fa-database" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                        </div>
                        <h6 style={{ color: '#495057', fontWeight: '600' }}>No Databases Found</h6>
                        <p style={{ color: '#6c757d', fontSize: '0.9rem', maxWidth: '350px', margin: '0 auto 15px' }}>
                          No databases match <strong>{getComplianceLabel(selectedCompliance)}</strong> + <strong>{selectedDatabaseType}</strong>.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setSelectedDatabaseType('');
                              setFilteredDatabases([]);
                            }}
                          >
                            <i className="fas fa-exchange-alt me-1"></i>
                            Try Different Type
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              setSelectedCompliance('');
                              setSelectedDatabaseType('');
                              setFilteredDatabases([]);
                            }}
                          >
                            <i className="fas fa-redo me-1"></i>
                            Start Over
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    border: '2px dashed #dee2e6'
                  }}>
                    <i className="fas fa-arrow-left" style={{ fontSize: '2rem', color: '#6c757d', marginBottom: '15px', display: 'block' }}></i>
                    <h6 style={{ color: '#495057' }}>Select Compliance &amp; Database Type</h6>
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                      Choose a compliance framework and database type from the left to see available databases.
                    </p>
                  </div>
                )}
              </div>
            </div>
      </WideModal>

      {/* Confirm Create Campaign Modal */}
      <WideModal
        show={showConfirmCampaignModal}
        onClose={() => setShowConfirmCampaignModal(false)}
        title="Create Campaign?"
        headerIcon="fa-check-circle"
        headerClass="bg-success text-white"
        maxWidth="700px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowConfirmCampaignModal(false)}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={executeCreateCampaign}
              disabled={isLoading}
            >
              <i className="fas fa-check me-2"></i>
              Create Campaign
            </button>
          </>
        }
      >
        <p className="text-muted mb-3">Review and confirm the campaign details below.</p>

        {confirmValidationError && (
          <div className="alert alert-danger py-2 mb-3">
            <i className="fas fa-exclamation-circle me-2"></i>
            {confirmValidationError}
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="confirmCampaignName" className="form-label fw-semibold">
            <i className="fas fa-flag me-2 text-muted"></i>Campaign Name
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="confirmCampaignName"
              value={baseCampaignName}
              onChange={handleCampaignNameChange}
              placeholder="Enter campaign name"
              autoFocus
            />
            <span className="input-group-text font-monospace text-muted" id="campaign-name-suffix">
              _{getComplianceLabel(selectedCompliance)}_{selectedDatabaseType}
            </span>
          </div>
          <small className="text-muted d-block">
            <i className="fas fa-info-circle me-1"></i>
            Suffix is locked — edit only the prefix. Final name: <code>{campaignName}</code>
          </small>
          <small className="text-muted d-block">
            <i className="fas fa-keyboard me-1"></i>
            Use only letters, numbers, spaces, hyphens (-), and underscores (_). Other characters are not allowed.
          </small>
        </div>

        <div className="mb-3">
          <label htmlFor="confirmTableName" className="form-label fw-semibold">
            <i className="fas fa-database me-2 text-muted"></i>Report Table Name
            <span className="text-muted fw-normal ms-1" style={{ fontSize: '0.85rem' }}>(PostgreSQL)</span>
          </label>
          <input
            type="text"
            className="form-control font-monospace"
            id="confirmTableName"
            value={tableName}
            onChange={handleTableNameChange}
            placeholder="uar_report_..."
            style={{ backgroundColor: '#f8f9fa' }}
          />
          <small className="text-muted">
            <i className="fas fa-database me-1"></i>
            Must contain only letters, numbers, and underscores.
          </small>
        </div>

        <div className="card bg-light border-0">
          <div className="card-body py-3">
            <h6 className="card-title mb-2">
              <i className="fas fa-info-circle me-2 text-info"></i>Campaign Summary
            </h6>
            <table className="mb-0" style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 0', color: '#666', width: '140px' }}>
                    <i className="fas fa-shield-alt me-2" style={{ width: '16px' }}></i>Compliance:
                  </td>
                  <td style={{ padding: '4px 0', fontWeight: '600' }}>{getComplianceLabel(selectedCompliance)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 0', color: '#666' }}>
                    <i className="fas fa-database me-2" style={{ width: '16px' }}></i>Database Type:
                  </td>
                  <td style={{ padding: '4px 0', fontWeight: '600' }}>{selectedDatabaseType}</td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 0', color: '#666' }}>
                    <i className="fas fa-server me-2" style={{ width: '16px' }}></i>Databases:
                  </td>
                  <td style={{ padding: '4px 0', fontWeight: '600' }}>{selectedDatabases.length} selected</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </WideModal>

      {/* Missing Database Connections Modal */}
      <WideModal
        show={showMissingConnectionsModal}
        onClose={() => { setShowMissingConnectionsModal(false); setShowConfirmCampaignModal(true); }}
        title="Missing Database Connections"
        headerIcon="fa-exclamation-triangle"
        headerClass="bg-warning text-dark"
        maxWidth="800px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowMissingConnectionsModal(false); setShowConfirmCampaignModal(true); }}
            >
              <i className="fas fa-times me-2"></i>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleProceedWithMissingConnections}
              disabled={isLoading}
            >
              <i className="fas fa-check me-2"></i>
              Proceed Anyway
            </button>
          </>
        }
      >
        <p className="mb-3">The following databases do not have source connections configured:</p>
        <div className="table-responsive mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
              <tr>
                <th>#</th>
                <th>Database Name</th>
                <th>Database Type</th>
              </tr>
            </thead>
            <tbody>
              {missingConnectionsData.missing.map((m, idx) => (
                <tr key={idx}>
                  <td className="text-muted">{idx + 1}</td>
                  <td className="fw-semibold">{m.database_name}</td>
                  <td>{m.database_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="alert alert-warning mb-0">
          <i className="fas fa-info-circle me-2"></i>
          <strong>Note:</strong> These databases will have no user data in the campaign until connections are configured in Database Connections.
        </div>
      </WideModal>

      {/* Campaign List Page */}
      {showCampaignListPage && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                {(selectedCompliance || selectedDatabaseType) && (
                  <div className="d-flex gap-2">
                    {selectedCompliance && (
                      <span className="badge badge-success" style={{
                        padding: 'var(--space-sm) var(--space-md)'
                      }}>
                        <i className="fas fa-shield-alt me-1"></i>
                        {getComplianceLabel(selectedCompliance)}
                      </span>
                    )}
                    {selectedDatabaseType && (
                      <span className="badge badge-info" style={{
                        padding: 'var(--space-sm) var(--space-md)'
                      }}>
                        <i className="fas fa-database me-1"></i>
                        {selectedDatabaseType}
                      </span>
                    )}
                    <span className="badge" style={{
                      backgroundColor: 'var(--background-secondary)',
                      color: 'var(--text-primary)',
                      padding: 'var(--space-sm) var(--space-md)'
                    }}>
                      {existingCampaigns.length} {existingCampaigns.length === 1 ? 'Campaign' : 'Campaigns'}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  if (onNavigateToDashboard) {
                    onNavigateToDashboard();
                  }
                  setShowCampaignListPage(false);
                  setShowCampaignActionDropdown(false);
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Dashboard
              </button>
            </div>
            <div>
                {/* Action Buttons */}
                {selectedCampaign && (
                  <div className="d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => {
                        const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
                        if (campaign && onCampaignSelect) {
                          onCampaignSelect(campaign);
                        }
                      }}
                    >
                      <i className="fas fa-play me-2"></i>
                      View Workflow
                    </button>
                    {isSelectedCampaignDeletable() && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={showDeleteConfirmation}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-trash me-2"></i>
                            Delete Campaign
                          </>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedCampaign(null);
                        checkExistingCampaigns(); // Refresh campaign list and clear selection
                      }}
                      disabled={isLoading}
                    >
                      <i className="fas fa-sync me-2"></i>
                      Refresh
                    </button>
                  </div>
                )}
                {selectedCampaign && (
                  <div className="alert alert-success mb-4" style={{
                    borderLeft: '4px solid var(--success)'
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-check-circle me-2" style={{color: 'var(--success)'}}></i>
                      <strong style={{color: 'var(--text-primary)'}}>Selected Campaign:</strong>
                    </div>
                    <div className="mt-2">
                      <div className="fw-semibold mb-2" style={{color: 'var(--text-primary)'}}>
                        {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_name}
                      </div>
                      <div className="d-flex gap-3">
                        <span>
                          <strong style={{color: 'var(--success)'}}>Status:</strong>
                          <span className="ms-1" style={{color: 'var(--text-primary)'}}>
                            {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.status}
                          </span>
                        </span>
                        <span>
                          <strong style={{color: 'var(--info)'}}>Compliance:</strong>
                          <span className="ms-1" style={{color: 'var(--text-primary)'}}>
                            {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.compliance_framework}
                          </span>
                        </span>
                        <span>
                          <strong style={{color: 'var(--warning)'}}>Database Type:</strong>
                          <span className="ms-1" style={{color: 'var(--text-primary)'}}>
                            {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.database_type}
                          </span>
                        </span>
                      </div>
                    </div>
                    {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_description && (
                      <small className="text-muted mt-2 d-block">
                        {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_description}
                      </small>
                    )}
                  </div>
                )}
                {existingCampaigns.length > 0 ? (
                  <div style={{width: '100%', overflowX: 'auto'}}>
                    <table className="table table-hover table-bordered table-sm" style={{width: '100%', tableLayout: 'auto', fontSize: 'var(--font-size-sm)'}}>
                      <thead style={{backgroundColor: 'var(--primary-100)'}}>
                        <tr>
                          <th style={{width: '40px', padding: 'var(--space-sm)'}} className="text-center">
                            <i className="fas fa-check-circle" style={{color: 'var(--success)'}} title="Select Campaign"></i>
                          </th>
                          <th style={{minWidth: '180px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Campaign Name</th>
                          <th style={{minWidth: '110px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Status</th>
                          <th style={{minWidth: '100px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Compliance</th>
                          <th style={{minWidth: '110px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Database Type</th>
                          <th style={{minWidth: '130px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Created By</th>
                          <th style={{minWidth: '140px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Created Date</th>
                          <th style={{minWidth: '140px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Last Updated</th>
                          <th style={{minWidth: '100px', padding: 'var(--space-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-semibold)'}}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {existingCampaigns.map(campaign => (
                          <tr
                            key={campaign.campaign_id}
                            className={!isDeletableCampaign(campaign) ? 'table-light' : ''}
                            style={{cursor: 'pointer'}}
                            onClick={() => handleCampaignSelection(campaign.campaign_id)}
                            onDoubleClick={() => {
                              if (onCampaignSelect) {
                                onCampaignSelect(campaign);
                              }
                            }}
                          >
                            <td className="text-center" style={{padding: 'var(--space-sm)'}} onClick={(e) => e.stopPropagation()}>
                              <input
                                type="radio"
                                name="campaignSelection"
                                className="form-check-input"
                                checked={selectedCampaign === campaign.campaign_id}
                                onChange={() => handleCampaignSelection(campaign.campaign_id)}
                                title="Select this campaign"
                              />
                            </td>
                            <td style={{whiteSpace: 'normal', padding: 'var(--space-sm)'}}>
                              <div className="fw-semibold" style={{color: 'var(--text-primary)'}}>
                                {campaign.campaign_name}
                              </div>
                              {campaign.campaign_description && (
                                <small className="text-muted d-block mt-1">{campaign.campaign_description}</small>
                              )}
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}}>
                              <span className="badge" style={{
                                backgroundColor:
                                  campaign.status === 'COMPLETED' ? 'var(--success)' :
                                  campaign.status === 'ACTIVE' ? 'var(--info)' :
                                  campaign.status === 'CREATED' ? 'var(--primary-700)' :
                                  campaign.status === 'ATTESTATION' ? 'var(--warning)' :
                                  campaign.status === 'RUNNING' ? 'var(--primary-700)' :
                                  campaign.status === 'FAILED' ? 'var(--danger)' :
                                  campaign.status === 'PAUSED' ? 'var(--warning)' :
                                  campaign.status === 'CANCELLED' ? 'var(--gray-600)' :
                                  'var(--info)',
                                color: 'var(--white)',
                                padding: 'var(--space-sm) var(--space-md)',
                                borderRadius: 'var(--radius-full)',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 'var(--font-weight-semibold)'
                              }}>
                                {campaign.status === 'ACTIVE' && <i className="fas fa-play-circle me-1"></i>}
                                {campaign.status === 'COMPLETED' && <i className="fas fa-check-circle me-1"></i>}
                                {campaign.status === 'ATTESTATION' && <i className="fas fa-clipboard-check me-1"></i>}
                                {campaign.status === 'FAILED' && <i className="fas fa-times-circle me-1"></i>}
                                {campaign.status === 'CANCELLED' && <i className="fas fa-ban me-1"></i>}
                                {campaign.status}
                              </span>
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}}>
                              <span className="badge badge-success" style={{
                                padding: 'var(--space-xs) var(--space-sm)'
                              }}>{campaign.compliance_framework}</span>
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}}>
                              <span className="badge badge-info" style={{
                                padding: 'var(--space-xs) var(--space-sm)'
                              }}>{campaign.database_type}</span>
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}}>
                              <div style={{color: 'var(--text-primary)'}}>
                                {campaign.created_by}
                                {campaign.is_owner && (
                                  <span className="badge bg-primary ms-2" title="You are the owner of this campaign">
                                    <i className="fas fa-user me-1"></i>You
                                  </span>
                                )}
                                {!campaign.can_modify && !campaign.is_owner && (
                                  <span className="badge bg-secondary ms-2" title="You cannot modify this campaign">
                                    <i className="fas fa-lock me-1"></i>Read-only
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}}>
                              <div style={{color: 'var(--text-primary)'}}>
                                {formatDateTime(campaign.created_date)}
                              </div>
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}}>
                              {campaign.last_modified_date ? (
                                <div style={{color: 'var(--text-primary)'}}>
                                  {formatDateTime(campaign.last_modified_date)}
                                </div>
                              ) : (
                                <span className="text-muted fst-italic">
                                  <i className="fas fa-minus-circle me-1"></i>
                                  Never
                                </span>
                              )}
                            </td>
                            <td style={{whiteSpace: 'nowrap', padding: 'var(--space-sm)'}} onClick={(e) => e.stopPropagation()}>
                              {campaign.status === 'ATTESTATION' && (() => {
                                const readiness = closureReadiness[campaign.campaign_id];
                                const isReady = readiness?.ready === true;
                                const pct = readiness?.completion_percentage;
                                const tooltip = isReady
                                  ? 'Close Campaign & Generate Evidence'
                                  : pct !== undefined
                                    ? `This button will be enabled when all attestations are complete. Currently ${readiness.completed_attestations} of ${readiness.total_users} completed (${pct}%).`
                                    : 'Checking closure readiness...';
                                return (
                                  <button
                                    className={`btn btn-sm ${isReady ? 'btn-outline-dark' : 'btn-outline-secondary'}`}
                                    onClick={() => {
                                      setCloseCampaignTarget(campaign);
                                      setShowCloseCampaignModal(true);
                                    }}
                                    disabled={!isReady}
                                    title={tooltip}
                                  >
                                    <i className={`fas ${isReady ? 'fa-archive' : 'fa-lock'} me-1`}></i>
                                    Close
                                  </button>
                                );
                              })()}
                              {campaign.status === 'COMPLETED' && (
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => {
                                    setEvidenceViewerCampaign(campaign);
                                    setShowEvidenceViewer(true);
                                  }}
                                  title="View Evidence Package"
                                >
                                  <i className="fas fa-folder-open me-1"></i>
                                  Evidence
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center" style={{padding: 'var(--space-3xl) var(--space-lg)'}}>
                    <i className="fas fa-inbox mb-4" style={{fontSize: 'var(--font-size-4xl)', color: 'var(--primary-600)'}}></i>
                    <h4 className="fw-bold mb-2" style={{color: 'var(--text-primary)'}}>No campaigns found</h4>
                    <p className="text-muted mb-3" style={{fontSize: 'var(--font-size-lg)'}}>Get started by creating your first UAR campaign</p>
                    <p style={{color: 'var(--text-secondary)'}}>
                      Click the <span className="fw-semibold" style={{color: 'var(--success)'}}>Create New Campaign</span> button to begin
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && ReactDOM.createPortal(
        <div tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ margin: 0, maxWidth: '600px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '2px solid #333' }}>
              {/* Header */}
              <div style={{ flexShrink: 0, backgroundColor: '#dc3545', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '20px', borderBottom: '2px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ color: '#fff', fontWeight: '600', margin: 0, fontSize: '1.25rem' }}>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Delete Campaign - Choose Action
                </h5>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmModal(false)}
                  aria-label="Close"
                  style={{ background: 'transparent', border: 'none', fontSize: '28px', fontWeight: '700', lineHeight: 1, color: '#fff', cursor: 'pointer', padding: '0', opacity: 0.8 }}
                  onMouseOver={(e) => e.target.style.opacity = '1'}
                  onMouseOut={(e) => e.target.style.opacity = '0.8'}
                >×</button>
              </div>

              {/* Body */}
              <div style={{ overflowY: 'auto', flex: '1 1 auto', backgroundColor: '#ffffff', padding: '24px' }}>
                <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '12px', marginBottom: '20px' }}>
                  <i className="fas fa-info-circle me-2" style={{ color: '#856404' }}></i>
                  <strong style={{ color: '#856404' }}>Campaign:</strong> <span style={{ color: '#856404' }}>{existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_name}</span>
                </div>
                <p style={{ marginBottom: '20px', color: '#212529' }}>
                  Please choose what to do with the campaign's report table:
                </p>
                <div style={{ marginBottom: '20px' }}>
                  <div className="form-check mb-3 p-3 border rounded" style={{
                    backgroundColor: deleteAction === 'archive' ? '#d4edda' : 'transparent',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDeleteAction('archive')}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deleteAction"
                      id="archiveAction"
                      value="archive"
                      checked={deleteAction === 'archive'}
                      onChange={(e) => setDeleteAction(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label w-100" htmlFor="archiveAction" style={{ cursor: 'pointer' }}>
                      <div className="d-flex align-items-start">
                        <i className="fas fa-archive fa-2x me-3 mt-1" style={{color: '#28a745'}}></i>
                        <div>
                          <strong className="d-block mb-1" style={{ color: '#212529' }}>Archive (Recommended)</strong>
                          <small style={{ color: '#6c757d' }}>
                            Rename the table to <code>TABLE_NAME_ARCHIVE_YYYYMMDD</code>
                            <br />
                            The table will be preserved and can be referenced later if needed.
                          </small>
                        </div>
                      </div>
                    </label>
                  </div>
                  <div className="form-check p-3 border rounded" style={{
                    backgroundColor: deleteAction === 'remove' ? '#f8d7da' : 'transparent',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDeleteAction('remove')}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deleteAction"
                      id="removeAction"
                      value="remove"
                      checked={deleteAction === 'remove'}
                      onChange={(e) => setDeleteAction(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label w-100" htmlFor="removeAction" style={{ cursor: 'pointer' }}>
                      <div className="d-flex align-items-start">
                        <i className="fas fa-trash-alt fa-2x me-3 mt-1" style={{color: '#dc3545'}}></i>
                        <div>
                          <strong className="d-block mb-1" style={{ color: '#212529' }}>Remove Permanently</strong>
                          <small style={{ color: '#6c757d' }}>
                            Drop the table completely from the database
                            <br />
                            <span className="fw-bold" style={{color: '#dc3545'}}>This action cannot be undone!</span>
                          </small>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ flexShrink: 0, backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: '16px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmModal(false)}
                  style={{ backgroundColor: '#6c757d', border: '1px solid #6c757d', borderRadius: '4px', color: '#fff', padding: '8px 16px', fontWeight: '500', cursor: 'pointer', fontSize: '1rem' }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteCampaign}
                  style={{
                    backgroundColor: deleteAction === 'archive' ? '#ffc107' : '#dc3545',
                    border: '1px solid ' + (deleteAction === 'archive' ? '#ffc107' : '#dc3545'),
                    borderRadius: '4px',
                    color: deleteAction === 'archive' ? '#000' : '#fff',
                    padding: '8px 16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = deleteAction === 'archive' ? '#e0a800' : '#c82333';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = deleteAction === 'archive' ? '#ffc107' : '#dc3545';
                  }}
                >
                  <i className={`fas ${deleteAction === 'archive' ? 'fa-archive' : 'fa-trash'} me-2`}></i>
                  {deleteAction === 'archive' ? 'Archive & Delete Campaign' : 'Remove & Delete Campaign'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Certification Error Modal */}
      <CertificationStatusModal
        show={showCertificationErrorModal}
        onClose={() => setShowCertificationErrorModal(false)}
        variant="error"
        title="Cannot Create Campaign"
        databaseType={certificationError.databaseType}
        totalCount={certificationError.totalCount || 0}
        certifiedCount={certificationError.certifiedCount || 0}
        uncertifiedCount={certificationError.uncertifiedCount || 0}
        alertMessage={(() => {
          // The blocker can be privileges, fixed roles, or both. Build a
          // message that names exactly what's pending so a roles-only blocker
          // (privileges 172/172 but roles unassigned) doesn't read as
          // "0 privileges uncertified".
          const pu = certificationError.uncertifiedCount || 0;
          const ru = certificationError.roleUncertifiedCount || 0;
          const rUnassigned = certificationError.roleUnassignedCount || 0;
          const parts = [];
          if (pu > 0) parts.push(<><strong>{pu}</strong> privilege{pu === 1 ? '' : 's'}</>);
          if (ru > 0) parts.push(<><strong>{ru}</strong> role{ru === 1 ? '' : 's'}</>);
          return (
            <>
              {parts.length === 0
                ? <>Certification for <strong>{certificationError.databaseType}</strong> is incomplete.</>
                : <>{parts.reduce((a, b) => <>{a} and {b}</>)} for{' '}
                    <strong>{certificationError.databaseType}</strong>{' '}
                    {(pu + ru) === 1 ? 'is' : 'are'} not yet certified.</>}
              {rUnassigned > 0 && <> <strong>{rUnassigned}</strong> of those role{rUnassigned === 1 ? '' : 's'} still need{rUnassigned === 1 ? 's' : ''} a privilege level assigned before {rUnassigned === 1 ? 'it' : 'they'} can be certified.</>}
            </>
          );
        })()}
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCertificationErrorModal(false)}
          >
            <i className="fas fa-times me-2"></i>
            Close
          </button>
        }
      >
        <div className="card mb-4" style={{ backgroundColor: 'var(--background-secondary)', border: 'none' }}>
          <div className="card-body">
            <h6 className="card-title mb-3" style={{ color: 'var(--primary-600)' }}>
              <i className="fas fa-info-circle me-2"></i>
              Why is Privilege Certification Important?
            </h6>
            <p className="mb-2">
              <strong>Privilege certification is essential for accurate classification of user privileges.</strong>
            </p>
            <ul className="mb-0">
              <li className="mb-2">
                <i className="fas fa-check-circle me-2 text-success"></i>
                Ensures proper privilege level assignment (READ, WRITE, ADMIN)
              </li>
              <li className="mb-2">
                <i className="fas fa-check-circle me-2 text-success"></i>
                Provides accurate risk assessment during attestation
              </li>
              <li>
                <i className="fas fa-check-circle me-2 text-success"></i>
                Maintains compliance with regulatory requirements
              </li>
            </ul>
          </div>
        </div>

        <div className="alert alert-info mb-0" style={{ border: 'none' }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-lightbulb fa-lg me-3 mt-1"></i>
            <div>
              <h6 className="alert-heading mb-2">Next Steps</h6>
              <ol className="mb-0 ps-3">
                <li className="mb-1">Navigate to <strong>"Admin Settings → Manage Privilege Certification"</strong></li>
                <li className="mb-1">Certify all <strong>{certificationError.databaseType}</strong> privileges</li>
                <li>Return here to create your campaign</li>
              </ol>
            </div>
          </div>
        </div>
      </CertificationStatusModal>

      {/* Evidence Viewer Modal */}
      <EvidenceViewer
        campaign={evidenceViewerCampaign}
        show={showEvidenceViewer}
        onClose={() => {
          setShowEvidenceViewer(false);
          setEvidenceViewerCampaign(null);
        }}
      />

      {/* Close Campaign Modal */}
      <CloseCampaignModal
        campaign={closeCampaignTarget}
        show={showCloseCampaignModal}
        onClose={() => {
          setShowCloseCampaignModal(false);
          setCloseCampaignTarget(null);
        }}
        onClosed={(result) => {
          // Refresh the campaigns list to show updated status
          setShowCloseCampaignModal(false);
          setCloseCampaignTarget(null);
          // Refresh campaigns list
          checkExistingCampaigns();
        }}
      />

    </div>
  );
};

export default UARCampaigns;
